/**
 * The day by the hour for the Day timeline's chart (user, 19/10/2026: "make
 * The day really professional"): 24 hourly buckets in REAL time from the
 * window's opening hour (or an earlier first drop), so a window past
 * midnight reads left to right in send order. Each bucket holds its drops
 * and SMS per container (board order — a container keeps its colour) and the
 * lists it sends to; whether the window is open in it. Skipped drops do not
 * count.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanPreview } from '../../../drop-generator.types';
import { liveDrop } from '../summary/timeline-facts';

const HOUR = 3_600_000;

export interface HourSlice {
  containerId: string;
  name: string;
  color: string;
  drops: number;
  sms: number;
}

export interface HourBucket {
  index: number;
  startIso: string;
  open: boolean;
  drops: number;
  sms: number;
  slices: HourSlice[];
  lists: string[];
}

type Container = DayPlanPreview['containers'][number];
type Drop = Container['drops'][number];

/** The first hour of the chart: the window's opening hour, or an earlier first drop's. */
export function dayStart(preview: DayPlanPreview): number {
  const first = preview.containers.flatMap((c) => c.drops.filter(liveDrop)).reduce((min, d) => Math.min(min, Date.parse(d.atUtc)), Infinity);
  return Math.floor(Math.min(Date.parse(preview.window.startUtc), first) / HOUR) * HOUR;
}

export function dayHours(preview: DayPlanPreview): HourBucket[] {
  const base = dayStart(preview);
  const ws = Date.parse(preview.window.startUtc);
  const we = Date.parse(preview.window.endUtc);
  const hours: HourBucket[] = Array.from({ length: 24 }, (_, index) => {
    const start = base + index * HOUR;
    return { index, startIso: new Date(start).toISOString(), open: start + HOUR > ws && start < we, drops: 0, sms: 0, slices: [], lists: [] };
  });
  for (const c of preview.containers)
    for (const d of c.drops.filter(liveDrop)) {
      const i = Math.floor((Date.parse(d.atUtc) - base) / HOUR);
      if (i >= 0 && i < 24) add(hours[i], c, d);
    }
  return hours;
}

function add(hour: HourBucket, c: Container, d: Drop): void {
  hour.drops += 1;
  hour.sms += d.amount;
  const slice = hour.slices.find((s) => s.containerId === c.containerId);
  if (slice) {
    slice.drops += 1;
    slice.sms += d.amount;
  } else hour.slices.push({ containerId: c.containerId, name: c.name, color: c.color, drops: 1, sms: d.amount });
  if (d.listName && !hour.lists.includes(d.listName)) hour.lists.push(d.listName);
}

/** Clean axis ticks from 0 to just above the max (steps of 1, 2 or 5 × 10ⁿ). */
export function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / 3;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  const ticks = [0];
  while (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
}
